import type { ReportingDeclaredBindings } from "./draft.generated";
import { repairLine } from "./rule-catalog";
import recipeManifest from "../../../../config/questionnaire-recipe-registry.json";
import { ASSET_FAMILY_FACTS, isAssetFamily } from "./asset-families";
import type {
  StudySpec,
  StudySpecExposureMode,
  StudySpecGoalId,
  StudySpecMaterial,
  StudySpecPerAssetReads,
  StudySpecRecipe,
  StudySpecRecorded,
  StudySpecRecordedContext,
  StudySpecStudyBrandEdit,
  StudySpecStudyBrandProvenance,
  StudySpecStudyBrandSource,
} from "./study-spec.generated";

export type * from "./study-spec.generated";

/**
 * The StudySpec resolver (plan Stage 3, S3.2).
 *
 * One pure function from typed facts to the spec: no I/O, no clock, no model.
 * It reads the recorded half, whether a questionnaire is built, catalog rows
 * `{id, type, pricingVisible?}`, the recipe manifest
 * (`config/questionnaire-recipe-registry.json`, generated from the Python
 * recipes) and the family registry
 * (`config/questionnaire-asset-families.json`). Objective text, question
 * wording, asset names, stable keys and tags are never inputs.
 *
 * It records, it never refuses on methodology (07 §5.0): a mixed or
 * unexpected family is `expected: false`, not an error. The only errors are
 * mechanical: an unknown family string, a missing or repeated asset id, and a
 * recorded recipe this build does not register.
 */

export const STUDY_SPEC_SCHEMA_VERSION = "study-spec.v1" as const;

/** One catalog row as the resolver reads it. */
export interface StudySpecCatalogRow {
  id: string;
  /** The asset family string as stored; an unknown one is refused. */
  type: string;
  /**
   * The asset's own typed pricing read (a landing page's
   * `metadata.lpAnalysis`, as the backend's `landingPageRead` reads it: an
   * image read, or a text read of its current copy). Absent for an asset with
   * no pricing read; null while its read is missing, unreadable or stale.
   */
  pricingVisible?: boolean | null;
}

export interface StudySpecResolverInput {
  recorded: StudySpecRecorded;
  /** Whether a questionnaire is built (`researchQuestions.length > 0`). */
  built: boolean;
  catalog: ReadonlyArray<StudySpecCatalogRow>;
}

export type StudySpecResolutionCode =
  | "asset_type_unknown"
  | "asset_id_invalid"
  | "recipe_unknown";

export class StudySpecResolutionError extends Error {
  constructor(
    readonly code: StudySpecResolutionCode,
    readonly path: string,
    message: string,
  ) {
    super(message);
    this.name = "StudySpecResolutionError";
  }
}

/** One decision a study of the recipe informs, by id (plan S3.3, L16); PLAN records it. */
export interface RecipeDecisionOutcomeDeclaration {
  id: string;
  /** Fits only a study with at least this many tested assets; PLAN reads it. */
  minAssets: number;
  /** Advised to PLAN, never required. */
  advisedModules: ReadonlyArray<string>;
  reportReads: ReadonlyArray<"subgroup">;
}

/**
 * One dimension of a declared composite: one question from any of `moduleIds`
 * (joined on `planAttribution.recipeModuleId`) feeds it.
 */
export interface RecipeCompositeDimensionDeclaration {
  moduleIds: ReadonlyArray<string>;
  label: string;
  weight: number;
  direction: "higher_is_better" | "lower_is_better";
  contributes: "rating" | "open_end";
  kind: string | null;
  conditional: boolean;
}

export interface RecipeCompositeDeclaration {
  compositeId: string;
  label: string;
  dimensions: ReadonlyArray<RecipeCompositeDimensionDeclaration>;
}

/**
 * What one registered recipe version declares (plan S3.3), as
 * `scripts/generate_recipe_manifest.py` projects it. Advice, records and
 * report shape; none of it gates a study (07 §5.0).
 */
export interface RecipeVersionDeclarations {
  /** This version's module universe, in declaration order. */
  moduleIds: ReadonlyArray<string>;
  goalIds: ReadonlyArray<string>;
  /**
   * The families this version is written for, each with who supplies its
   * material: the user (the opening chain asks for it once when the brief
   * gave none) or PLAN (written from the conversation; never asked).
   */
  materials: "open" | ReadonlyArray<{ family: string; suppliedBy: "user" | "plan" }>;
  exposurePolicy: { perAssetReads: StudySpecPerAssetReads };
  /** What the reply calls the instrument (`ReplyStudyFacts.instrument`), or null. */
  instrumentLabel: string | null;
  methodologyFamilies: ReadonlyArray<
    "general" | "landing_page_conversion" | "pain_opportunity"
  >;
  intakeAsks: ReadonlyArray<"pricing_visible">;
  decisionOutcomes: ReadonlyArray<RecipeDecisionOutcomeDeclaration>;
  moduleAttribution: Readonly<Record<string, "per_asset" | "joint">>;
  /** When each module's read is asked relative to the stimulus. */
  moduleExposure: Readonly<
    Record<string, "pre_and_post" | "post_exposure" | "unbound" | "pre_exposure">
  >;
  /**
   * The modules that read a brand the respondent has not been shown
   * (`RecipeModule.brandUndisclosed`).
   */
  brandUndisclosedModuleIds: ReadonlyArray<string>;
  /**
   * The question types each module's legal shapes take, its own declared
   * type first (`RecipeModule.legal_shapes`; plan + draft guards).
   */
  moduleShapeTypes: Readonly<Record<string, ReadonlyArray<string>>>;
  reporting: {
    composites: ReadonlyArray<RecipeCompositeDeclaration>;
    verdict: {
      kind: "readiness";
      winnerCompositeId: string;
      supportCompositeId: string;
    } | null;
    deckLayout: "landing_page" | null;
    /**
     * What each report binding reads, by module and column role, as a build
     * stamps it (`reporting_resolver.declared_report_bindings`).
     */
    bindings: ReportingDeclaredBindings;
  };
}

interface RecipeManifestEntry {
  latestVersion: string;
  moduleIds: ReadonlyArray<string>;
  versions: Record<string, RecipeVersionDeclarations>;
}

const RECIPES = (
  recipeManifest as unknown as {
    recipes: Record<string, RecipeManifestEntry>;
  }
).recipes;

/** The goal index over the latest versions, built once. */
function indexGoals(): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const [recipeId, entry] of Object.entries(RECIPES)) {
    const latest = entry.versions[entry.latestVersion];
    if (!latest) {
      throw new TypeError(
        `Recipe manifest has no declarations for ${recipeId}@${entry.latestVersion}.`,
      );
    }
    for (const value of latest.goalIds) {
      if (index.has(value)) {
        throw new TypeError(
          `Recipe manifest: goalIds value ${value} is declared by ${index.get(value)} and ${recipeId}.`,
        );
      }
      index.set(value, recipeId);
    }
  }
  return index;
}

const RECIPE_BY_GOAL = indexGoals();

function latestRecipe(
  recipeId: string,
  source: StudySpecRecipe["source"],
): StudySpecRecipe {
  return { recipeId, recipeVersion: RECIPES[recipeId].latestVersion, source };
}

/**
 * Rule 1. A built questionnaire freezes the recorded recipe, and a recipe a
 * built plan recorded is always kept. A built questionnaire with no recorded
 * recipe has none: it reports through the custom slots and is never
 * re-inferred (step 1 audit decision; L12, L17). Before the first build the
 * goal decides: the latest version of the recipe that lists it in `goalIds`.
 * With no goal there is no recipe. (Stage 3a retired the router's method as a
 * selector: nothing writes one before the first build.)
 */
function resolveRecipe(input: StudySpecResolverInput): StudySpecRecipe | null {
  const recorded = input.recorded.recipe;
  if (recorded && (input.built || recorded.source === "recorded_plan")) {
    return { ...recorded };
  }
  if (input.built) return null;
  const goalId: StudySpecGoalId | null = input.recorded.goalId;
  return goalId ? goalRecipe(goalId) : null;
}

/**
 * The recipe a goal's new study starts from: the latest version of the recipe
 * whose `goalIds` lists the goal (rule 1, before the first build), or null for
 * a goal no recipe lists. Intake reads it before a draft has a spec to store
 * (the opening card, the pasted material's family).
 */
export function goalRecipe(goalId: string): StudySpecRecipe | null {
  const recipeId = RECIPE_BY_GOAL.get(goalId);
  return recipeId ? latestRecipe(recipeId, "goal") : null;
}

/**
 * The declarations of one registered recipe version, or null when this build
 * does not register it.
 */
export function recipeDeclarations(recipe: {
  recipeId: string;
  recipeVersion: string;
}): RecipeVersionDeclarations | null {
  const entry = Object.prototype.hasOwnProperty.call(RECIPES, recipe.recipeId)
    ? RECIPES[recipe.recipeId]
    : undefined;
  return entry &&
    Object.prototype.hasOwnProperty.call(entry.versions, recipe.recipeVersion)
    ? entry.versions[recipe.recipeVersion]
    : null;
}

/**
 * The deck layout the study's recorded recipe declares, or null (plan Stage 3,
 * S3.4 rows 21 and 30): the pre-results half of Python's `declared_layout`
 * over `_recorded_report_recipe`. A study with no recorded recipe, or a
 * user-defined one, reports through the custom slots and declares none.
 */
export function declaredDeckLayout(
  spec: StudySpec | null | undefined,
): RecipeVersionDeclarations["reporting"]["deckLayout"] {
  const recipe = spec?.recorded.recipe;
  if (!recipe || spec?.recorded.scope === "user_defined") return null;
  return recipeDeclarations(recipe)?.reporting.deckLayout ?? null;
}

/**
 * The verdict the study's recorded recipe declares, or null: the composites a
 * positioning map plots when the recipe declares no axes (plots plan O,
 * `quadrantAxisMethod`), the pre-results half of Python's
 * `AnalysisReportingPlan.verdict`. Same recipe rule as `declaredDeckLayout`.
 */
export function declaredVerdict(
  spec: StudySpec | null | undefined,
): RecipeVersionDeclarations["reporting"]["verdict"] {
  const recipe = spec?.recorded.recipe;
  if (!recipe || spec?.recorded.scope === "user_defined") return null;
  return recipeDeclarations(recipe)?.reporting.verdict ?? null;
}

function declarationsOf(recipe: StudySpecRecipe): RecipeVersionDeclarations {
  const declarations = recipeDeclarations(recipe);
  if (!declarations) {
    throw new StudySpecResolutionError(
      "recipe_unknown",
      "recorded.recipe",
      repairLine("recipe_unknown", { recipeId: String(recipe.recipeId), recipeVersion: String(recipe.recipeVersion) }),
    );
  }
  return declarations;
}

/** Rule 2. Role from the family registry; `expected` from the recipe. */
function resolveMaterials(
  catalog: ReadonlyArray<StudySpecCatalogRow>,
  declarations: RecipeVersionDeclarations | null,
): Array<StudySpecMaterial> {
  const seen = new Set<string>();
  return catalog.map((row, index) => {
    if (!isAssetFamily(row.type)) {
      throw new StudySpecResolutionError(
        "asset_type_unknown",
        `catalog[${index}].type`,
        repairLine("asset_type_unknown", { type: String(JSON.stringify(row.type)) }),
      );
    }
    if (typeof row.id !== "string" || !row.id || seen.has(row.id)) {
      throw new StudySpecResolutionError(
        "asset_id_invalid",
        `catalog[${index}].id`,
        repairLine("asset_id_invalid"),
      );
    }
    seen.add(row.id);
    const family = row.type;
    return {
      assetId: row.id,
      family,
      role: ASSET_FAMILY_FACTS[family].exposureBound ? "stimulus" : "catalog",
      expected:
        declarations !== null &&
        (declarations.materials === "open" ||
          declarations.materials.some(
            (material) => material.family === family,
          )),
    };
  });
}

/**
 * Rule 4, today's tri-state: any asset read true is true; reads that are all
 * false are false; otherwise the recorded card answer, else unknown.
 */
function resolvePricingVisible(
  catalog: ReadonlyArray<StudySpecCatalogRow>,
  pricingAnswer: boolean | null,
): boolean | null {
  const reads = catalog
    .filter((row) => row.pricingVisible !== undefined)
    .map((row) => row.pricingVisible);
  if (reads.some((read) => read === true)) return true;
  if (reads.length > 0 && reads.every((read) => read === false)) return false;
  return pricingAnswer;
}

/**
 * The spec a draft stores (S3.1): every reader goes through this, and no
 * reader resolves one. Null when the draft carries none (a draft whose last
 * commit predates Stage 3).
 */
export function studySpecOf(
  draft: { studySpec?: StudySpec | null } | null | undefined,
): StudySpec | null {
  const spec = draft?.studySpec;
  return spec && spec.schemaVersion === STUDY_SPEC_SCHEMA_VERSION ? spec : null;
}

function brandNameOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/**
 * The draft's "Study brand" record (`draft.studyBrand`): the user's override,
 * or the provenance of the recorded brands' `brandName`.
 */
export type StudySpecStudyBrand =
  | StudySpecStudyBrandEdit
  | StudySpecStudyBrandProvenance;

/** The study's brand and where it came from (`resolveStudyBrand`). */
export interface ResolvedStudyBrand {
  brandName: string;
  source: StudySpecStudyBrandSource;
}

/**
 * The study's brand and where it came from (plan Decision 5), or null, read
 * off a spec's `recorded.context`. Python's `resolve_study_brand` applies the
 * same rule (`tests/fixtures/study-spec/study-brand-cases.json`): the user's
 * edit overrides, and its null name is an explicit "no brand"; else the
 * recorded brands' `brandName`, with the source a stored provenance record
 * names (`org`), else `objective`; else no brand, which is every study before
 * the field existed. A provenance record never names a brand of its own.
 */
export function resolveStudyBrand(
  context: StudySpecRecordedContext | null | undefined,
): ResolvedStudyBrand | null {
  const stored = context?.studyBrand;
  if (stored?.source === "user_edit") {
    const name = brandNameOf(stored.brandName);
    return name ? { brandName: name, source: "user_edit" } : null;
  }
  const name = brandNameOf(context?.brands?.brandName);
  return name ? { brandName: name, source: stored?.source ?? "objective" } : null;
}

export function resolveStudySpec(input: StudySpecResolverInput): StudySpec {
  const recipe = resolveRecipe(input);
  const declarations = recipe ? declarationsOf(recipe) : null;
  const materials = resolveMaterials(input.catalog, declarations);
  const stimulus = materials.filter((material) => material.role === "stimulus");
  const count = stimulus.length;
  // Rule 3. One asset is never a comparison (F18, C6).
  const mode: StudySpecExposureMode =
    count === 0
      ? "none"
      : count === 1
        ? "single"
        : input.recorded.exposure.choice?.value === "monadic"
          ? "isolated"
          : "shared";
  const recorded = JSON.parse(
    JSON.stringify(input.recorded),
  ) as StudySpecRecorded;
  return {
    schemaVersion: STUDY_SPEC_SCHEMA_VERSION,
    recorded: { ...recorded, recipe },
    resolved: {
      context: {
        pricingVisible: resolvePricingVisible(
          input.catalog,
          input.recorded.context.pricingAnswer,
        ),
      },
      materials,
      stimulus: {
        families: [...new Set(stimulus.map((material) => material.family))],
        count,
      },
      exposure: {
        mode,
        perAssetReads:
          declarations?.exposurePolicy.perAssetReads ?? "follow_design",
      },
      design: mode === "isolated" ? "twin_monadic" : "comparative",
    },
  };
}
